import { create } from 'zustand';
import type { Card, RelicCombatMods } from '../game/types';
import { initialState, reducer, type GameAction } from '../game/engine';
import { getStarterDeckIdsForLibrary, resolveInitialDeckIds } from '../config/starterDeck';
import { INITIAL_DECK_MIN_SIZE, REQUIRE_BOTH_KINDS, canRemoveDeckCard } from '../game/deckRules';

type Store = {
  state: ReturnType<typeof initialState>;
  dispatch: (action: GameAction) => void;

  library: Card[];
  enemyLibrary: Card[];

  // Unique deck: true = owned/selected, false = not in deck
  selected: Record<string, boolean>;

  loadCards: (cards: Card[]) => void;
  toggleSelect: (cardId: string) => void;
  selectAll: () => void;
  clearSelection: () => void;
  applyProvidedPreset: () => void;

  startCombat: (enemyId?: string) => void;
  backToSetup: () => void;

  addCardToDeck: (cardId: string) => void;
  removeCardFromDeck: (cardId: string, minDeckSizeOverride?: number) => void;

  setDeckFromIds: (ids: string[]) => void;
  addCardToDeckNoRestart: (cardId: string) => void;

  setRelicMods: (mods: RelicCombatMods) => void;
};

function deckFromSelected(library: Card[], selected: Record<string, boolean>) {
  return library.filter((c) => !!selected[c.id]);
}

function buildSelectedMapFromIds(library: Card[], ids: string[]) {
  const byId = new Set(library.map((c) => c.id));
  const nextSelected: Record<string, boolean> = {};
  for (const c of library) nextSelected[c.id] = false;

  const seen = new Set<string>();
  for (const rawId of Array.isArray(ids) ? ids : []) {
    const id = String(rawId ?? '');
    if (!id || seen.has(id) || !byId.has(id)) continue;
    seen.add(id);
    nextSelected[id] = true;
  }

  return nextSelected;
}

function topUpSelectionToMinimum(library: Card[], selected: Record<string, boolean>) {
  const next = { ...selected };

  const ensureKind = (kind: 'physical' | 'magic') => {
    const deck = deckFromSelected(library, next);
    const count = deck.filter((c) => c.kind === kind).length;
    if (count > 0) return;

    const pick = library.find((c) => c.kind === kind && !next[c.id]);
    if (pick) next[pick.id] = true;
  };

  if (REQUIRE_BOTH_KINDS) {
    ensureKind('physical');
    ensureKind('magic');
  }

  let deck = deckFromSelected(library, next);
  if (deck.length < INITIAL_DECK_MIN_SIZE) {
    for (const c of library) {
      if (deck.length >= INITIAL_DECK_MIN_SIZE) break;
      if (next[c.id]) continue;
      next[c.id] = true;
      deck = deckFromSelected(library, next);
    }
  }

  return next;
}

export const useGameStore = create<Store>((set, get) => ({
  state: initialState(),
  dispatch: (action) => set((s) => ({ state: reducer(s.state, action) })),

  library: [],
  enemyLibrary: [],
  selected: {},

  loadCards: (cards) => {
    const enemyDeck = cards.filter((c) => c.kind === 'enemy');
    const guiltTemplate = cards.find((c) => String(c.title ?? '').trim().toLowerCase() === 'guilt' || String((c as any).specialType ?? '').trim().toLowerCase() === 'affliction') ?? null;
    const library = cards.filter((c) => c.kind !== 'enemy' && !c.acquisitionLocked);
    const starterIds = new Set(getStarterDeckIdsForLibrary(library));

    const selected: Record<string, boolean> = {};
    for (const c of library) selected[c.id] = starterIds.has(c.id);

    const deck = deckFromSelected(library, selected);

    set({ library, enemyLibrary: enemyDeck, selected });
    get().dispatch({ type: 'LOAD_SUCCESS', payload: { deck, enemyDeck, guiltTemplate } });
  },

  toggleSelect: (cardId) => {
    const { selected, library } = get();
    const isOn = !!selected[cardId];
    const currentDeck = deckFromSelected(library, selected);

    if (isOn && !canRemoveDeckCard(currentDeck, cardId, INITIAL_DECK_MIN_SIZE, REQUIRE_BOTH_KINDS)) return;

    const nextSelected = { ...selected, [cardId]: !isOn };
    const deck = deckFromSelected(library, nextSelected);

    set({ selected: nextSelected });
    get().dispatch({ type: 'SET_PLAYER_DECK', deck });
  },

  selectAll: () => {
    const { library } = get();
    const nextSelected: Record<string, boolean> = {};
    for (const c of library) nextSelected[c.id] = true;

    const deck = deckFromSelected(library, nextSelected);
    set({ selected: nextSelected });
    get().dispatch({ type: 'SET_PLAYER_DECK', deck });
  },

  clearSelection: () => {
    const { library } = get();
    const nextSelected: Record<string, boolean> = {};
    for (const c of library) nextSelected[c.id] = false;

    const minimumSelected = topUpSelectionToMinimum(library, nextSelected);
    const deck = deckFromSelected(library, minimumSelected);

    set({ selected: minimumSelected });
    get().dispatch({ type: 'SET_PLAYER_DECK', deck });
  },

  applyProvidedPreset: () => {
    const { library } = get();
    const starterIds = resolveInitialDeckIds(library, undefined);
    const nextSelected = buildSelectedMapFromIds(library, starterIds);
    const deck = deckFromSelected(library, nextSelected);

    set({ selected: nextSelected });
    get().dispatch({ type: 'SET_PLAYER_DECK', deck });
  },

  startCombat: (enemyId) => {
    if (typeof enemyId === 'string' && enemyId.trim()) {
      get().dispatch({ type: 'START_COMBAT', enemyId: enemyId.trim() });
      return;
    }
    get().dispatch({ type: 'START_COMBAT' });
  },

  backToSetup: () => {
    get().dispatch({ type: 'BACK_TO_SETUP' });
  },

  addCardToDeck: (cardId) => {
    const { selected, library } = get();

    if (selected[cardId]) {
      get().dispatch({ type: 'START_COMBAT' });
      return;
    }

    const nextSelected = { ...selected, [cardId]: true };
    const deck = deckFromSelected(library, nextSelected);

    set({ selected: nextSelected });
    get().dispatch({ type: 'SET_PLAYER_DECK', deck });
    get().dispatch({ type: 'START_COMBAT' });
  },

  removeCardFromDeck: (cardId, minDeckSizeOverride) => {
    const { selected, library } = get();

    if (!selected[cardId]) {
      get().dispatch({ type: 'START_COMBAT' });
      return;
    }

    const currentDeck = deckFromSelected(library, selected);
    const effectiveMinDeckSize = Math.max(0, Number(minDeckSizeOverride) || INITIAL_DECK_MIN_SIZE);
    if (!canRemoveDeckCard(currentDeck, cardId, effectiveMinDeckSize, REQUIRE_BOTH_KINDS)) return;

    const nextSelected = { ...selected, [cardId]: false };
    const deck = deckFromSelected(library, nextSelected);

    set({ selected: nextSelected });
    get().dispatch({ type: 'SET_PLAYER_DECK', deck });
    get().dispatch({ type: 'START_COMBAT' });
  },

  setDeckFromIds: (ids) => {
    const { library } = get();
    const resolvedIds = resolveInitialDeckIds(library, ids);
    const nextSelected = buildSelectedMapFromIds(library, resolvedIds);
    const deck = deckFromSelected(library, nextSelected);

    set({ selected: nextSelected });
    get().dispatch({ type: 'SET_PLAYER_DECK', deck });
  },

  addCardToDeckNoRestart: (cardId) => {
    const { selected, library } = get();
    if (selected[cardId]) return;

    const nextSelected = { ...selected, [cardId]: true };
    const deck = deckFromSelected(library, nextSelected);

    set({ selected: nextSelected });
    get().dispatch({ type: 'SET_PLAYER_DECK', deck });
  },

  setRelicMods: (mods) => {
    get().dispatch({ type: 'SET_RELIC_MODS', mods });
  },
}));
